import type {
  BrandAssetHash,
  ResolvedBrandFont,
} from "@ascleo/frontdesk-contracts/branding/model";
import { safeBrandFontFamily } from "./branding-css";
import type {
  BrandingResourceLoader,
  LoadedBrandFont,
} from "./branding-types";

function abortError(): DOMException {
  return new DOMException("Branding staging was aborted.", "AbortError");
}

function bytesForBlob(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

/**
 * Create the browser implementation for blob URLs, image decode, and FontFace lifecycle.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { makeBrowserBrandingResourceLoader } from "@ascleo/frontdesk-shared/branding";
 *
 * const loader = makeBrowserBrandingResourceLoader();
 * ```
 */
export function makeBrowserBrandingResourceLoader(): BrandingResourceLoader {
  const nativeFonts = new WeakMap<LoadedBrandFont, FontFace>();

  return {
    createUrl: (asset) =>
      URL.createObjectURL(
        new Blob([bytesForBlob(asset.bytes)], {
          type: asset.metadata.mediaType,
        }),
      ),
    loadImage: (url, signal) =>
      new Promise<void>((resolve, reject) => {
        if (signal.aborted) {
          reject(abortError());
          return;
        }
        const image = new Image();
        const cleanup = () => {
          signal.removeEventListener("abort", abort);
          image.removeEventListener("load", loaded);
          image.removeEventListener("error", failed);
        };
        const abort = () => {
          cleanup();
          image.src = "";
          reject(abortError());
        };
        signal.addEventListener("abort", abort, { once: true });
        const loaded = () => {
          cleanup();
          resolve();
        };
        const failed = () => {
          cleanup();
          reject(new Error("Branding image decode failed."));
        };
        image.addEventListener("load", loaded, { once: true });
        image.addEventListener("error", failed, { once: true });
        image.src = url;
      }),
    loadFont: async (
      font: ResolvedBrandFont,
      assetHash: BrandAssetHash,
      url: string,
      signal: AbortSignal,
    ): Promise<LoadedBrandFont> => {
      if (signal.aborted) throw abortError();
      const family = safeBrandFontFamily(font).split(",")[0]?.replaceAll('"', "");
      if (family === undefined || family.length === 0) {
        throw new Error("Branding font family is invalid.");
      }
      const descriptor = font.faces.find((face) => face.assetHash === assetHash);
      if (descriptor === undefined) {
        throw new Error("Branding font face descriptor is missing.");
      }
      const nativeFont = new FontFace(
        family,
        `url("${url}")`,
        {
          style: descriptor.style,
          weight: `${descriptor.weightMin} ${descriptor.weightMax}`,
          display: font.display,
        },
      );
      let rejectAbort: ((reason: DOMException) => void) | undefined;
      const abort = new Promise<never>((_resolve, reject) => {
        rejectAbort = reject;
      });
      const aborted = () => rejectAbort?.(abortError());
      signal.addEventListener("abort", aborted, { once: true });
      try {
        await Promise.race([nativeFont.load(), abort]);
      } finally {
        signal.removeEventListener("abort", aborted);
      }
      const handle: LoadedBrandFont = { family, assetHash };
      nativeFonts.set(handle, nativeFont);
      return handle;
    },
    registerFont: (font) => {
      const nativeFont = nativeFonts.get(font);
      if (nativeFont === undefined) throw new Error("Loaded font handle expired.");
      document.fonts.add(nativeFont);
    },
    verifyFont: (font) =>
      [400, 500, 600, 700].every((weight) =>
        document.fonts.check(`${weight} 1em "${font.family}"`),
      ),
    removeFont: (font) => {
      const nativeFont = nativeFonts.get(font);
      if (nativeFont !== undefined) {
        document.fonts.delete(nativeFont);
        nativeFonts.delete(font);
      }
    },
    revokeUrl: (url) => URL.revokeObjectURL(url),
  };
}
